import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ConversationColumn.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ConversationColumnProps {
  /**
   * The stage width. 400 in all 63 thread artboards, and the reason the cap exists at all — the
   * panel is that wide, so a bubble capped at 320 and a card stretched to 400 are drawn against a
   * known measure rather than against whatever the viewport happens to be.
   *
   * It is a **cap**, not a fixed width: below it the column is fluid. Raising it is legitimate for
   * a wider panel; the numbers inside a turn do not move when you do.
   */
  width?: number;
  /**
   * Turns, bare blocks, or a mix. There is no wrapper requirement — `Agent/Done` puts a user row,
   * a paragraph, an artifact card and an action row here as four direct children, while
   * `Founder · cold start` puts a `DayDivider` and three `ConversationTurn`s. Both are correct, and
   * both land on the same 12px rhythm, which is the whole reason this box has nothing else in it.
   */
  children: React.ReactNode;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The 400px column every thread in the chat is drawn on: a flex column, 12px apart, top-aligned.
 *
 * Six extraction passes proposed it under six names — `ConversationThread`, `AgentTurn`,
 * `ToolCardStack`, `ThreadColumn`, "the content column" — because it is the root of every board
 * that holds a thread and each pass met it wearing whatever its own column happened to hold. It is
 * one box, and it holds nothing of its own: no padding, no ground, no rule, no scroll. The panel
 * around it owns the ground; the parts inside own their chrome.
 *
 * **The 17 chat boards are deliberately not this component.** INVENTORY §1.46 opens on "all 80" and
 * then takes 17 of them back in the next sentence: "The chat column's stage differs:
 * `justify-content: flex-end` with `gap: 8px`, because the composer bottom-aligns." chat.md:23-24
 * transcribes that second frame in full and it disagrees on three of the four numbers that make
 * this one — `508` wide, not 400; `flex-end`, not `flex-start`; `8`, not the 12 that is the whole
 * point of the file. It is also not a thread: its only child is the composer, and it exists to push
 * that card onto the artboard's bottom edge. Modelling both stages through one prop would put a
 * second separator inside the component whose entire claim is that there is only one, so the
 * composer's stage stays the panel's business and this box stays the thread's.
 *
 * **Deliberately not a live region.** A thread is the textbook `role="log"`, but the parts that
 * actually change under a reader already carry their own polite regions (`ActivityRow`'s label,
 * `ActivityList`, `QueueMeta`), and a live region nested inside another one gets announced
 * twice — once as its own mutation, once as the ancestor's. Putting the region on the leaf that
 * moves is both quieter and more accurate about *what* moved, so this box stays silent on purpose.
 */
export function ConversationColumn({
  width = 400,
  children,
  theme: themeProp,
  className,
}: ConversationColumnProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(chatThemeClass(theme), styles.column, className)}
      // The one runtime value in the component, handed to CSS as a custom property rather than as
      // an inline `width` — the stylesheet keeps the `100%` + cap relationship that makes the
      // column fluid, and this only moves where the cap sits.
      style={{ '--cv15-column-width': `${width}px` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}
